import React, { useMemo } from 'react';
import Skeleton from 'react-loading-skeleton';

import { Button } from '@eika/button';
import { InfoCard } from '@eika/card';
import { IconTypes } from '@eika/icon';
import { Link, LinkType } from '@eika/link';
import { ListPanel, ListPanelWrapper } from '@eika/link-panel';
import { RichIcon, RichIconTypes } from '@eika/rich-icon';

import { LoanList } from '../../components/LoanList';
import { Content } from '../../components/shared/Content';
import { ErrorCardWrapper } from '../../components/shared/ErrorCardWrapper';
import { useConfig } from '../../defaultConfig';
import { allLoanAccountsSelector, useAccounts } from '../../fetchHooks/useAccount';
import { useEkbLoans } from '../../fetchHooks/useEkbLoans';
import useTranslation from '../../hooks/useTranslation';
import { isFlexiLoanAccount, isLoanAccount } from '../../services/accountFilters';
import { loansTrackingIDs } from '../../utils/gtm/trackIDs/loans';
import { LoanTotal } from './LoanTotal';

import './LoanOverview.scss';

const LoanOverview: React.FC = () => {
    const translated = useTranslation();
    const { loanLink, smalanLink, accountLink, salgspantlaanLink, featureToggles, getLoanLink } = useConfig();
    const { data: allAccounts, ...accountState } = useAccounts({ select: allLoanAccountsSelector });
    const loans = useMemo(() => (allAccounts ?? []).filter(isLoanAccount), [allAccounts]);
    const flexiLoans = useMemo(() => (allAccounts ?? [])?.filter(isFlexiLoanAccount), [allAccounts]);
    const trackID = loansTrackingIDs;

    const { data: ekbLoans, ...ekbState } = useEkbLoans(featureToggles.enableEkbLoans);

    const ekbSmalan = ekbLoans?.filter((account) => account.coreProduct === 'SMALAN') ?? [];
    const ekbSalgspantlan = ekbLoans?.filter((account) => account.coreProduct === 'SALGSPANT_LAAN') ?? [];

    const exception = accountState.error || ekbState.error;

    const hasNoLoans = loans.length === 0 && (ekbLoans ?? []).length === 0 && flexiLoans.length === 0;

    const isLoading = accountState.isLoading || ekbState.isLoading;

    if (hasNoLoans && !isLoading && !exception) {
        return (
            <Content className="loan-overview">
                <InfoCard img={<RichIcon type={RichIconTypes.HUS_M_BIL} />}>
                    <InfoCard.TitleH2>{translated.loanOverviewNoLoansHeader}</InfoCard.TitleH2>
                    <p>{translated.loanOverviewNoLoansText}</p>
                    {getLoanLink && (
                        <Link
                            linkType={LinkType.BUTTON_STANDARD_POSITIVE}
                            href={getLoanLink.url}
                            data-track-id={trackID.loanOverview}
                            data-track-linkname={translated.loans.seeOurLoans}
                        >
                            {translated.loans.seeOurLoans}
                        </Link>
                    )}
                </InfoCard>
            </Content>
        );
    }

    const combinedLoans = [...loans, ...flexiLoans, ...ekbSmalan, ...ekbSalgspantlan];

    return (
        <Content className="loan-overview">
            <ListPanelWrapper
                contentWrapperAs="ol"
                data-testid={!isLoading ? 'loan-overview-list' : 'loader'}
                footerContent={!isLoading && combinedLoans.length > 1 && <LoanTotal accounts={combinedLoans} />}
                footerClassName="loan-overview__footer"
            >
                {hasNoLoans && isLoading ? (
                    <li className="sgw-link-panel loan-overview--skeleton">
                        <Skeleton height={20} />
                        <Skeleton width="30%" />
                    </li>
                ) : (
                    <>
                        <LoanList loans={loans} url={loanLink?.url} />
                        <LoanList loans={flexiLoans} url={accountLink?.url} />
                        <LoanList loans={ekbSmalan} url={smalanLink?.url} />
                        <LoanList loans={ekbSalgspantlan} url={salgspantlaanLink?.url} />
                    </>
                )}
                {getLoanLink && (
                    <ListPanel
                        invertIcon
                        className="create-account-link"
                        url={getLoanLink.url}
                        title={translated.newLoan.title}
                        textTwo={translated.newLoan.description}
                        icon={IconTypes.PLUSS_DYN}
                        as="li"
                        data-track-id={trackID.loanOverview}
                        data-track-linkname={translated.newLoan.title}
                        data-testid="start-create-loan"
                        backgroundColor="light"
                    />
                )}
                {accountState.isError && !accountState.isFetching && (
                    <ErrorCardWrapper
                        className="loan-overview__error"
                        title={translated.errors.oops}
                        exception={accountState.error}
                        defaultError={translated.loanOverviewErrorMessage}
                    >
                        <Button
                            onClick={() => accountState.refetch()}
                            data-track-id={trackID.loanOverviewError}
                            data-track-linkname={translated.tryAgain}
                        >
                            {translated.tryAgain}
                        </Button>
                    </ErrorCardWrapper>
                )}
                {ekbState.isError && !ekbState.isFetching && (
                    <ErrorCardWrapper
                        className="loan-overview__error"
                        title={translated.errors.oops}
                        exception={ekbState.error}
                        defaultError={translated.loanOverviewErrorMessage}
                    >
                        <Button
                            onClick={() => ekbState.refetch()}
                            data-track-id={trackID.loanOverviewError}
                            data-track-linkname={translated.tryAgain}
                        >
                            {translated.tryAgain}
                        </Button>
                    </ErrorCardWrapper>
                )}
            </ListPanelWrapper>
        </Content>
    );
};

export default LoanOverview;
